import React, { useLayoutEffect, useRef, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import themeManager from '../../../utils/themeManager';

gsap.registerPlugin(ScrollTrigger);

const stores = [
  { name: "Ušće",       img: "/assets/images/mi-store-usce.webp" },
  { name: "Delta",      img: "/assets/images/mi-store-delta.webp" },
  { name: "BEO",        img: "/assets/images/mi-store-beo.webp" },
  { name: "Kragujevac", img: "/assets/images/mi-store-kg.webp" },
  { name: "Ada Mall",   img: "/assets/images/mi-store-ada.webp" },
  { name: "Galerija",   img: "/assets/images/mi-store-galerija.webp" },
  { name: "Niš",        img: "/assets/images/mi-store-nis.webp" },
];

const BG_START = "#0a0a0a";
const BG_END   = { dark: "#0F1115", light: "#F3F4F6" };

const WATERMARK = {
  dark:  { color: "rgba(255,255,255,0.13)", stroke: "rgba(255,255,255,0.06)" },
  light: { color: "rgba(0,0,0,0.08)",       stroke: "rgba(0,0,0,0.05)"       },
};

// Mobile title starts a bit more visible since it's the "hero" before image arrives
const MOBILE_TITLE_INITIAL = {
  color: "rgba(255,255,255,0.28)",
  stroke: "rgba(255,255,255,0.14)",
};

const XiaomiSection = () => {
  const { t } = useTranslation("home");
  const location = useLocation();
  const [isDarkMode, setIsDarkMode] = useState(themeManager.isDark());
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" ? window.innerWidth < 640 : false
  );

  useEffect(() => {
    const handleThemeChange = (e) => setIsDarkMode(e.detail.theme === "dark");
    window.addEventListener("themeChanged", handleThemeChange);
    return () => window.removeEventListener("themeChanged", handleThemeChange);
  }, []);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const sectionRef        = useRef(null);
  const imageContainerRef = useRef(null);
  const imageRef          = useRef(null);
  const titleRef          = useRef(null);
  const line1Ref          = useRef(null);
  const line2Ref          = useRef(null);
  const infoCardRef       = useRef(null);
  const taglineRef        = useRef(null);
  const sliderTrackRef    = useRef(null);

  useLayoutEffect(() => {
    const themeKey = isDarkMode ? "dark" : "light";
    const bgEnd    = BG_END[themeKey];
    const wm       = WATERMARK[themeKey];

    gsap.set(sectionRef.current, { backgroundColor: BG_START });

    if (isMobile) {
      // Mobile: title is the first visual — make it slightly visible
      gsap.set(line1Ref.current, {
        color: MOBILE_TITLE_INITIAL.color,
        WebkitTextStroke: `1px ${MOBILE_TITLE_INITIAL.stroke}`,
        opacity: 1,
      });
      gsap.set(line2Ref.current, {
        color: MOBILE_TITLE_INITIAL.color,
        WebkitTextStroke: `1px ${MOBILE_TITLE_INITIAL.stroke}`,
        opacity: 1,
      });
    } else {
      gsap.set(line1Ref.current, {
        color: WATERMARK.dark.color,
        WebkitTextStroke: `1px ${WATERMARK.dark.stroke}`,
        opacity: 1,
      });
      gsap.set(line2Ref.current, {
        color: WATERMARK.dark.color,
        WebkitTextStroke: `1px ${WATERMARK.dark.stroke}`,
        opacity: 1,
      });
    }

    const ctx = gsap.context(() => {

      /* ════════════════════════════════════════════════════════
         MOBILE TIMELINE
         ────────────────────────────────────────────────────────
         Initial state  : dark bg, title centered & visible,
                          image hidden below viewport
         Phase 1 (0→1.8): Image rises from below, covers screen
                           Title fades out as image passes over it
         Phase 2 (1.1→) : Tagline card + info badge fade in
         Phase 3 (>-0.2): Slider pans from right to left
      ════════════════════════════════════════════════════════ */
      if (isMobile) {

        gsap.set(imageContainerRef.current, {
          y: window.innerHeight * 1.05,
          force3D: true,
        });
        gsap.set(imageRef.current,       { scale: 1.08, force3D: true });
        gsap.set(infoCardRef.current,    { opacity: 0, x: 20, scale: 0.97, force3D: true });
        gsap.set(taglineRef.current,     { opacity: 0, y: 24, force3D: true });
        gsap.set(sliderTrackRef.current, { x: window.innerWidth, force3D: true });

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top top",
            // Longer scroll = slower, more deliberate on narrow screens
            end: () => `+=${sliderTrackRef.current.scrollWidth * 1.6}px`,
            scrub: 1.4,
            pin: true,
            anticipatePin: 1,
            fastScrollEnd: true,
            invalidateOnRefresh: true,
          },
        });

        // BG switch after image has mostly settled
        tl.to(sectionRef.current, {
          backgroundColor: bgEnd,
          duration: 0.5,
          ease: "power1.inOut",
        }, 1.0);

        // Phase 1: image rises, title fades as image covers it
        tl
          .to(imageContainerRef.current, {
            y: 0,
            duration: 1.8,
            ease: "expo.out",
            force3D: true,
          }, 0)
          .to(imageRef.current, {
            scale: 1,
            duration: 2.2,
            ease: "expo.out",
            force3D: true,
          }, 0)
          // Title starts fading once image is ~halfway up
          .to([line1Ref.current, line2Ref.current], {
            opacity: 0,
            duration: 0.7,
            ease: "power2.in",
          }, 0.65);

        // Phase 2: overlays appear after image has settled
        tl
          .to(infoCardRef.current, {
            opacity: 1, x: 0, scale: 1,
            duration: 0.9, ease: "power3.out", force3D: true,
          }, 1.15)
          .to(taglineRef.current, {
            opacity: 1, y: 0,
            duration: 0.7, ease: "power2.out", force3D: true,
          }, 1.35);

        // Phase 3: slider pan
        tl.to(sliderTrackRef.current, {
          x: () => -(sliderTrackRef.current.scrollWidth - window.innerWidth + 24),
          ease: "none",
          force3D: true,
        }, ">-0.2");

      } else {
        /* ════════════════════════════════════════════════════
           DESKTOP TIMELINE (original, unchanged)
        ════════════════════════════════════════════════════ */

        gsap.set(imageContainerRef.current, { yPercent: 60, scale: 0.94, force3D: true });
        gsap.set(imageRef.current,          { scale: 1.12, force3D: true });
        gsap.set(infoCardRef.current,       { opacity: 0, x: 48, scale: 0.97, force3D: true });
        gsap.set(taglineRef.current,        { opacity: 0, y: 28, force3D: true });
        gsap.set(sliderTrackRef.current,    { x: window.innerWidth, force3D: true });

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top top",
            end: () => `+=${sliderTrackRef.current.scrollWidth * 1.1}px`,
            scrub: 1.2,
            pin: true,
            anticipatePin: 1,
            fastScrollEnd: true,
            invalidateOnRefresh: true,
          },
        });

        tl.to(sectionRef.current, {
          backgroundColor: bgEnd,
          duration: 0.6,
          ease: "power1.inOut",
        }, 0.9)
        .to([line1Ref.current, line2Ref.current], {
          color: wm.color,
          WebkitTextStroke: `1px ${wm.stroke}`,
          duration: 0.6,
          ease: "power1.inOut",
        }, 0.9);

        tl
          .to(imageContainerRef.current, {
            yPercent: 0, scale: 1,
            duration: 1.5, ease: "expo.out", force3D: true,
          }, 0)
          .to(imageRef.current, {
            scale: 1,
            duration: 2, ease: "expo.out", force3D: true,
          }, 0)
          .to(titleRef.current, {
            yPercent: -18,
            duration: 1.5, ease: "power2.out", force3D: true,
          }, 0.2)
          .to([line1Ref.current, line2Ref.current], {
            opacity: 0.1,
            duration: 1.2, ease: "power2.inOut",
          }, 0.55)
          .to(infoCardRef.current, {
            opacity: 1, x: 0, scale: 1,
            duration: 1, ease: "power3.out", force3D: true,
          }, 0.85)
          .to(taglineRef.current, {
            opacity: 1, y: 0,
            duration: 0.8, ease: "power2.out", force3D: true,
          }, 1.05);

        tl.to(sliderTrackRef.current, {
          x: () => -(sliderTrackRef.current.scrollWidth - window.innerWidth + 48),
          ease: "none",
          force3D: true,
        }, ">-0.3");
      }

    }, sectionRef);

    return () => ctx.revert();
  }, [isDarkMode, isMobile]);

  const href = location.pathname.startsWith("/en")
    ? "/en/xiaomi#stores-map"
    : "/xiaomi#stores-map";

  return (
    <section
      ref={sectionRef}
      className="relative w-full h-screen overflow-hidden"
    >

      {/* ── TITLE ─────────────────────────────────────────── */}
      {/*
        Mobile  : vertically centered — this is the first thing the user sees.
                  GSAP fades it out as the image rises over it.
        Desktop : top-aligned, GSAP moves it upward on scroll.
      */}
      <div
        className="absolute left-0 right-0 px-4 sm:px-6 lg:px-14 pointer-events-none z-0"
        style={
          isMobile
            ? { top: "50%", transform: "translateY(-55%)" }
            : { top: "8vh" }
        }
      >
        <div className="max-w-[95rem] mx-auto">
          <div ref={titleRef} className="will-change-transform">
            <h1 className="leading-[0.86] tracking-[-0.04em]">
              <div
                ref={line1Ref}
                className="select-none"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 900,
                  fontSize: "clamp(2.8rem, 13vw, 11rem)",
                  color: WATERMARK.dark.color,
                  WebkitTextStroke: `1px ${WATERMARK.dark.stroke}`,
                  willChange: "color, opacity",
                  textAlign: isMobile ? "center" : "left",
                }}
              >
                {t("xiaomi.line1") || "RETAIL"}
              </div>
              <div
                ref={line2Ref}
                className="select-none -mt-2 sm:-mt-5 lg:-mt-8"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 900,
                  fontSize: "clamp(2.8rem, 13vw, 12rem)",
                  color: WATERMARK.dark.color,
                  WebkitTextStroke: `1px ${WATERMARK.dark.stroke}`,
                  willChange: "color, opacity",
                  textAlign: isMobile ? "center" : "left",
                }}
              >
                {t("xiaomi.line2") || "EXCELLENCE"}
              </div>
            </h1>
          </div>
        </div>
      </div>

      {/* ── MAIN IMAGE ────────────────────────────────────── */}
      {/*
        Mobile : fills the entire section (inset-0) — GSAP slides it in from
                 below, creating a full-screen cover effect over the title.
        Desktop: centered with max-width + aspect ratio as before.
      */}
      <div
        className="absolute z-10"
        style={
          isMobile
            ? { inset: 0 }
            : { inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }
        }
      >
        <div
          ref={imageContainerRef}
          style={
            isMobile
              ? { position: "absolute", inset: 0, willChange: "transform" }
              : {
                  position: "relative",
                  width: "100%",
                  maxWidth: "74rem",
                  aspectRatio: "16/9",
                  padding: "0 1rem",
                  willChange: "transform",
                }
          }
        >
          <div
            className="relative w-full h-full overflow-hidden"
            style={{ borderRadius: isMobile ? "0" : "16px" }}
          >
            <img
              ref={imageRef}
              src={`/assets/images/mi-store-ns${isMobile ? '-mobile' : ''}.webp`}
              alt="Xiaomi Store Novi Sad"
              decoding="async"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ willChange: "transform" }}
            />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(to top, rgba(0,0,0,0.62) 0%, transparent 52%)," +
                  "linear-gradient(to right, rgba(0,0,0,0.25) 0%, transparent 60%, rgba(0,0,0,0.1) 100%)",
              }}
            />
          </div>

          {/* Info Card — positioned inside the image wrapper */}
          <div
            ref={infoCardRef}
            style={{
              position: "absolute",
              ...(isMobile
                ? { bottom: "2rem", right: "1rem" }
                : {}),
              willChange: "transform, opacity",
            }}
            className={isMobile ? "" : "absolute bottom-3 right-4 sm:bottom-5 sm:right-6 lg:bottom-8 lg:right-8"}
          >
            <div
              className="flex items-center gap-2 sm:gap-3 px-3 py-2 sm:px-4 sm:py-3"
              style={{
                background: "rgba(10,10,10,0.72)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                borderRadius: "10px",
                border: "1px solid rgba(255,255,255,0.09)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
              }}
            >
              <CheckCircle2 size={14} className="text-[#ff6900] shrink-0" />
              <div>
                <span className="block text-white font-bold text-base sm:text-lg leading-none">8</span>
                <span className="text-[0.55rem] sm:text-[0.6rem] text-white/40 uppercase tracking-[0.18em] mt-0.5 block">
                  {t("xiaomi.locations") || "Lokacija"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── TAGLINE CARD ──────────────────────────────────── */}
      <div
        ref={taglineRef}
        className="absolute z-20"
        style={{
          // Mobile: slider strip (~5.5rem) + paddingBottom (3.5rem) = ~9rem total
          // add a small gap on top of that
          bottom: isMobile
            ? "calc(9.5rem + env(safe-area-inset-bottom, 0px))"
            : "clamp(4.5rem, 8vw, 7rem)",
          left: isMobile ? "0.75rem" : "clamp(1rem, 3vw, 3rem)",
          maxWidth: isMobile
            ? "min(300px, calc(100vw - 1.5rem))"
            : "clamp(260px, 80vw, 340px)",
          willChange: "transform, opacity",
        }}
      >
        <div
          className="p-4 sm:p-5 lg:p-8"
          style={{
            background: "rgba(8,8,8,0.82)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            borderRadius: "12px",
            border: "1px solid rgba(255,255,255,0.07)",
          }}
        >
          <div className="flex items-center gap-2.5 mb-2.5 sm:mb-3">
            <div
              className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-white font-bold text-xs sm:text-sm shrink-0"
              style={{ background: "#ff6900", borderRadius: "8px" }}
            >
              mi
            </div>
            <span className="text-white font-bold text-base sm:text-lg tracking-tight">
              {t("xiaomi.storeTitle") || "Xiaomi Store"}
            </span>
          </div>

          <div className="w-full h-px mb-2.5 sm:mb-3" style={{ background: "rgba(255,255,255,0.08)" }} />

          <p className="text-white/50 text-xs leading-relaxed mb-4 sm:mb-5 sm:text-sm">
            {t("xiaomi.description") ||
              "PC Centar je direktno uključen u razvoj, logistiku i upravljanje zvaničnim Xiaomi Store lokacijama."}
          </p>

          <a
            href={href}
            className="flex items-center gap-2 font-medium group transition-all"
            style={{ color: "#ff6900", width: "fit-content" }}
          >
            <span className="text-xs sm:text-sm group-hover:opacity-75 transition-opacity">
              {t("xiaomi.viewLocations") || "Pogledaj lokacije"}
            </span>
            <ArrowRight
              size={13}
              className="group-hover:translate-x-1.5 transition-transform duration-200"
            />
          </a>
        </div>
      </div>

      {/* ── STORE SLIDER ──────────────────────────────────── */}
      {/*
        FIX: padding-bottom uses env(safe-area-inset-bottom) so the cards
        are never hidden behind the iPhone home indicator / browser bar.
      */}
      <div
        className="absolute bottom-0 left-0 w-full z-30 pointer-events-none"
        style={{
          // Mobile: 3.5rem base guarantees clearance from browser nav bar
          // even on Android where env(safe-area-inset-bottom) is often 0.
          // Desktop: minimal padding is fine.
          paddingBottom: isMobile
            ? "calc(3.5rem + env(safe-area-inset-bottom, 0px))"
            : "calc(0.4rem + env(safe-area-inset-bottom, 0px))",
        }}
      >
        <div
          ref={sliderTrackRef}
          className="flex items-end gap-2 sm:gap-4 w-max px-2 sm:px-6"
          style={{ willChange: "transform" }}
        >
          {stores.map((store, i) => (
            <div
              key={i}
              className="relative shrink-0"
              style={{
                width: isMobile ? "clamp(6.5rem, 34vw, 9rem)" : "clamp(9rem, 20vw, 15rem)",
                aspectRatio: "4/3",
                borderRadius: isMobile ? "8px" : "10px",
                overflow: "hidden",
              }}
            >
              <img
                src={store.img.replace('.webp', `${isMobile ? '-mobile' : ''}.webp`)}
                alt={store.name}
                decoding="async"
                loading="lazy"
                className="w-full h-full object-cover"
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)",
                }}
              />
              <span
                className="absolute top-1.5 right-1.5 text-white/30 tracking-[0.1em]"
                style={{ fontFamily: "monospace", fontSize: "0.42rem" }}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="absolute bottom-0 left-0 right-0 px-2 py-1.5 sm:px-3 sm:py-2">
                <span
                  className="text-white font-semibold tracking-wide"
                  style={{ fontSize: isMobile ? "0.58rem" : "0.75rem" }}
                >
                  {store.name}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

    </section>
  );
};

export default XiaomiSection;